import { useQuery } from '@tanstack/react-query';
import {
  agentsApi,
  collaborationConfigsApi,
  environmentsApi,
  knowledgeBasesApi,
  modelConfigsApi,
  mcpConnectionsApi,
  promptsApi,
} from '@/services/api';
import type { DependentsEntity, DependentsResponse } from '@/types/dependents';

/**
 * What else points at a resource, read before offering to delete it (task 864).
 *
 * One query per entity + id, enabled only while a delete dialog is actually
 * open — the caller passes `enabled: false` (or an undefined id) everywhere
 * else, so a list of forty agents does not fetch forty dependent sets to render
 * forty menu items nobody opened.
 *
 * The reader is an explicit table rather than a path built from the entity
 * name: an entity with no `/dependents` route simply has no entry here, so a
 * surface that asks for one it does not have is a compile error instead of a
 * runtime 404.
 */
const DEPENDENTS_READER: Record<
  DependentsEntity,
  (id: string, scope?: 'platform') => Promise<DependentsResponse>
> = {
  agents: (id) => agentsApi.getDependents(id).then((res) => res.data.data),
  prompts: (id) => promptsApi.getDependents(id).then((res) => res.data.data),
  'collaboration-configs': (id) =>
    collaborationConfigsApi.getDependents(id).then((res) => res.data.data),
  'mcp-connections': (id) => mcpConnectionsApi.getDependents(id).then((res) => res.data.data),
  'knowledge-bases': (id) => knowledgeBasesApi.getDependents(id).then((res) => res.data.data),
  'model-configs': (id, scope) => modelConfigsApi.getDependents(id, scope).then((res) => res.data.data),
  environments: (id) => environmentsApi.getDependents(id).then((res) => res.data.data),
};

/**
 * `scope: 'platform'` asks for dependents across every account (task 1181).
 * Only a Model config's reader passes it on, and only platform staff may ask;
 * every other entity reads its own account whatever is passed.
 */
export function useDependents(
  entity: DependentsEntity,
  id: string | undefined,
  enabled = true,
  scope?: 'platform'
) {
  return useQuery<DependentsResponse>({
    queryKey: ['dependents', entity, id, scope ?? 'account'],
    queryFn: () => DEPENDENTS_READER[entity](id!, scope),
    enabled: enabled && !!id,
    // The answer is only ever read to fill one dialog, and it must not be a
    // stale one: a User who just repointed the last AI Service off a config
    // should be able to reopen the dialog and see the refusal gone.
    staleTime: 0,
    retry: false,
  });
}
